import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'assets/styles/pages/task-clearance-plan.scss';
import { Accordion, AccordionItem } from 'react-accessible-accordion';
import { TCPNavigationStatuses } from 'consts/TCP';
import { ButtonComponent } from 'component/Button';
import { ReactComponent as CloseSvg } from 'assets/images/icons/close_24px.svg';
import { ReactComponent as PencilSvg } from 'assets/images/icons/mode_edit_24px.svg';
import { ReactComponent as SendSvg } from 'assets/images/icons/send.svg';
import { Loader } from 'component/Loader';
import { useFormActions } from 'hooks/useDailyFormActions';
import { useDailyFormManagement } from 'hooks/useDailyFormManagement';
import { NavigationTabLinks } from '../../Tabs/NavigationLinks';
import { DMSBlock } from '../Blocks/DMSBlock/DMSBlock';
import { FieldWorkBlock } from '../Blocks/FieldWorkBlock/FieldWorkBlock';
import { useDailyFormErrors } from 'hooks/useDailyFormErrors';
import { EOFoundBlock } from '../Blocks/EOFound/EOFound';

export const DailyEditPage = () => {
  const { clearanceId, dailyId } = useParams();
  const [navigationStatuses, setNavigationStatuses] = useState(TCPNavigationStatuses);
  const [errorResponse, setErrorResponse] = useState(null);

  const {
    formData,
    loader,
    gridGeojson,
    originalGridGeojson,
    haReport,
    mapLoader,
    coords3857,
    setGridGeojson,
    zone,
    dispatch,
    crosshair,
    eoFoundChoices,
  } = useDailyFormManagement(clearanceId, dailyId);

  const { errors, hasError, validateForm } = useDailyFormErrors(errorResponse, formData, dispatch);

  const createData = useMemo(() => {
    if (!formData?.info) {
      return { type: 'Empty' };
    }

    const haReportId = formData?.info?.ha_report_id;
    const clearanceTaskId = formData?.info?.clearance_task_id;

    if (haReportId && !clearanceTaskId) {
      return { type: 'HA' };
    }

    if (clearanceTaskId && !haReportId) {
      return { type: 'TCP' };
    }

    return { type: 'Empty' };
  }, [formData?.info?.ha_report_id, formData?.info?.clearance_task_id]);

  const {
    sendButtonDisabled,
    handleSaveButton,
    handleDraftButton,
    handleCancelButton,
  } = useFormActions(clearanceId, setErrorResponse, dailyId, dispatch, validateForm, null, createData);

  useEffect(() => {
    setNavigationStatuses((prev) => ({
      ...prev,
      tcpCreate: 'completed',
      daily: 'active',
      weekly: 'inactive',
      completed: 'inactive',
      done: 'inactive',
    }));
    document.title = 'Update daily';
  }, []);

  return (
    <section>
      <div className="flex-column-group">
        {false && (
          <NavigationTabLinks
            statuses={navigationStatuses}
            mode="view"
            id={clearanceId}
          />
        )}
        <DailyEditForm
         formData={formData}
         dispatch={dispatch}
         coords3857={coords3857}
         zone={zone}
         setGridGeojson={setGridGeojson}
         gridGeojson={gridGeojson}
         mapLoader={mapLoader}
         haReportPolygon={haReport?.info?.turn_points?.geojson}
         errorResponse={errorResponse}
         errors={errors}
         hasError={hasError}
         crosshair={crosshair}
         eoFoundChoices={eoFoundChoices}
         clearanceId={clearanceId}
         createData={createData}
        />
        <div className="form-buttons buttons-group py-10">
          <ButtonComponent
            iconLeft={<PencilSvg />}
            handleButton={() => handleDraftButton(formData, gridGeojson, originalGridGeojson)}
            label="Draft"
            disabled={sendButtonDisabled}
            className="warning"
          />
          <ButtonComponent
            iconLeft={<SendSvg />}
            handleButton={() => handleSaveButton(formData, gridGeojson, originalGridGeojson)}
            label="Send"
            disabled={sendButtonDisabled}
          />
          <ButtonComponent
            iconLeft={<CloseSvg />}
            handleButton={handleCancelButton}
            label="Cancel"
            disabled={false}
            className="danger"
          />
        </div>
      </div>
      <Loader visible={loader} />
      <ToastContainer />
    </section>
  );
};

const DailyEditForm = ({
  formData,
  dispatch,
  coords3857,
  zone,
  gridGeojson,
  mapLoader,
  haReportPolygon,
  setGridGeojson,
  errorResponse,
  errors,
  hasError,
  crosshair,
  eoFoundChoices,
  clearanceId,
  createData,
}) => {
  const isDataReady = formData && dispatch;
    const isEmptyType = clearanceId === 'empty' ||
    (formData?.info?.clearance_task_id === null && formData?.info?.ha_report_id === null);
    const isHAType = formData?.info?.ha_report_id !== null && formData?.info?.clearance_task_id === null;
    const isTCPType = formData?.info?.clearance_task_id !== null && formData?.info?.ha_report_id === null;

  return (<div className="daily-form">
    <div className="form-container flex-column-group">
      <DailyFormHeader />
      <Accordion
        allowMultipleExpanded
        allowZeroExpanded
        preExpanded={[
          'dms', 'fieldWork', 'marking', 'battle-area-clearance', 'eoFound']}
        className="custom-accordion"
      >
        <AccordionItem uuid="dms">
          <DMSBlock
            formData={formData}
            dispatch={dispatch}
            hasError={hasError}
            isEmptyType={isEmptyType}
            isHAType={isHAType}
          />
        </AccordionItem>
        {isDataReady && formData?.info?.dms?.team_activity === 'field_work' && (
          <AccordionItem uuid="fieldWork">
            <FieldWorkBlock
            formData={formData}
            dispatch={dispatch}
            coords3857={coords3857}
            zone={zone}
            gridGeojson={gridGeojson}
            loader={mapLoader}
            setGridGeojson={setGridGeojson}
            haReportPolygon={haReportPolygon}
            errorResponse={errorResponse}
            errors={errors}
            hasError={hasError}
            mode="edit"
            crosshair={crosshair}
            createData={createData}
            />
          </AccordionItem>
        )}
        {isDataReady && formData?.info?.dms?.team_activity === 'eo_found' && (
          <AccordionItem uuid="eoFound">
            <EOFoundBlock
              formData={formData}
              dispatch={dispatch}
              errors={errors}
              hasError={hasError}
              eoFoundChoices={eoFoundChoices}
            />
          </AccordionItem>
          )}
      </Accordion>
    </div>
  </div>
  );
}


const DailyFormHeader = () => (
  <div className="form-header">
    <span>Update a daily</span>
    <div className="line" />
  </div>
);
